import { useEffect, useState, type ReactNode } from "react";
import { Code2, MessageSquare, PanelRightClose, PanelRightOpen } from "lucide-react";
import { BrandSiteLivePreview } from "@/components/brand-site/BrandSiteLivePreview";
import { BrandSiteSourceEditorCore, BrandSiteSourceEditorFullscreen } from "@/components/brand-site/BrandSiteSourceEditorPanel";
import { HermesDrawer } from "@/components/hermes/HermesDrawer";
import { Button } from "@/components/ui/button";
import type { BrandSiteCollection, BrandSitePage } from "@/lib/brand-site-api";
import { cn } from "@/lib/utils";

type RightPanel = "hermes" | "source";

type BrandSiteStudioProps = {
  accountId: string;
  brandSiteId: number;
  pages: BrandSitePage[];
  collections?: BrandSiteCollection[];
  selectedSlug: string | null;
  draftRevision: number;
  assetBaseUrl: string | null | undefined;
  onSelectSlug: (slug: string) => void;
  onPageSaved?: (page: BrandSitePage) => void;
  onPickSubmit?: (payload: {
    userRequest: string;
    pageSlug: string;
    pageTitle: string | null;
    pagePath: string;
    picks: import("@/lib/brand-site-pick-refs").PickRef[];
  }) => void | Promise<void>;
  pickSubmitting?: boolean;
  /** 顶栏左侧（标题等） */
  headerStart?: ReactNode;
  /** 顶栏右侧操作 */
  headerEnd?: ReactNode;
  /** 外部控制全屏源代码（如页面栏目入口） */
  sourceFullscreenOpen?: boolean;
  onSourceFullscreenOpenChange?: (open: boolean) => void;
  /** 进入页时默认右栏（如 ?source=1） */
  initialRightPanel?: RightPanel;
  className?: string;
};

/**
 * 设计工作室壳：左预览 + 右 AI / 源代码切换。
 */
export function BrandSiteStudio({
  accountId,
  brandSiteId,
  pages,
  collections,
  selectedSlug,
  draftRevision,
  assetBaseUrl,
  onSelectSlug,
  onPageSaved,
  onPickSubmit,
  pickSubmitting,
  headerStart,
  headerEnd,
  sourceFullscreenOpen,
  onSourceFullscreenOpenChange,
  initialRightPanel,
  className,
}: BrandSiteStudioProps) {
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [rightPanel, setRightPanel] = useState<RightPanel>(initialRightPanel ?? "hermes");
  const [localFullscreen, setLocalFullscreen] = useState(false);

  const fullscreenOpen = sourceFullscreenOpen ?? localFullscreen;
  const setFullscreenOpen = onSourceFullscreenOpenChange ?? setLocalFullscreen;

  useEffect(() => {
    if (initialRightPanel) setRightPanel(initialRightPanel);
  }, [initialRightPanel]);

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", className)}>
      <header className="flex shrink-0 items-center gap-3 border-b border-border bg-background px-3 py-2">
        <div className="min-w-0 flex-1">{headerStart}</div>
        <div className="flex shrink-0 items-center gap-2">
          {headerEnd}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => setRightCollapsed((v) => !v)}
            aria-pressed={!rightCollapsed}
            aria-label={rightCollapsed ? "打开右栏" : "收起右栏"}
          >
            {rightCollapsed ? (
              <PanelRightOpen className="size-4" aria-hidden />
            ) : (
              <PanelRightClose className="size-4" aria-hidden />
            )}
            {rightCollapsed ? "右栏" : "收起"}
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-2">
          <BrandSiteLivePreview
            accountId={accountId}
            brandSiteId={brandSiteId}
            pages={pages}
            collections={collections}
            selectedSlug={selectedSlug}
            draftRevision={draftRevision}
            assetBaseUrl={assetBaseUrl}
            onSelectSlug={onSelectSlug}
            onPickSubmit={onPickSubmit}
            pickSubmitting={pickSubmitting}
          />
        </section>

        {!rightCollapsed ? (
          <aside className="flex min-h-0 w-[380px] shrink-0 flex-col overflow-hidden border-l border-border">
            <div className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-1.5">
              <Button
                type="button"
                size="sm"
                variant={rightPanel === "hermes" ? "secondary" : "ghost"}
                className="h-8 flex-1 gap-1.5 text-xs"
                onClick={() => setRightPanel("hermes")}
              >
                <MessageSquare className="size-3.5" />
                AI
              </Button>
              <Button
                type="button"
                size="sm"
                variant={rightPanel === "source" ? "secondary" : "ghost"}
                className="h-8 flex-1 gap-1.5 text-xs"
                onClick={() => setRightPanel("source")}
              >
                <Code2 className="size-3.5" />
                源代码
              </Button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {rightPanel === "hermes" ? (
                <HermesDrawer variant="embedded" />
              ) : (
                <BrandSiteSourceEditorCore
                  accountId={accountId}
                  brandSiteId={brandSiteId}
                  pages={pages}
                  selectedSlug={selectedSlug}
                  onSelectSlug={onSelectSlug}
                  onSaved={onPageSaved}
                  showFullscreenButton
                  onRequestFullscreen={() => setFullscreenOpen(true)}
                />
              )}
            </div>
          </aside>
        ) : null}
      </div>

      <BrandSiteSourceEditorFullscreen
        open={fullscreenOpen}
        onOpenChange={setFullscreenOpen}
        accountId={accountId}
        brandSiteId={brandSiteId}
        pages={pages}
        selectedSlug={selectedSlug}
        onSelectSlug={onSelectSlug}
        onSaved={onPageSaved}
      />
    </div>
  );
}
